import { forwardRef, type Ref } from 'react';

import Checkmark from '@crehana/icons/outline/Checkmark';
import CloseSmall from '@crehana/icons/outline/CloseSmall';
import Plus from '@crehana/icons/outline/Plus';
import {
  colors,
  InverseButton,
  PrimaryButton,
  TextField,
  type TextFieldProps,
  // biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
} from '@crehana/ui';

interface TextFieldActionsProps extends TextFieldProps {
  isDark?: boolean;
  maxLength?: number;
  submitDisabled?: boolean;
  valueLength?: number;
  noCounter?: boolean;
  onCancel?: () => void;
}

const TextFieldActions = (
  {
    isDark,
    maxLength = 50,
    submitDisabled = false,
    valueLength = 0,
    noCounter = false,
    onCancel,
    ...props
  }: TextFieldActionsProps,
  ref: Ref<HTMLInputElement>,
) => {
  const counterClass = `font-legals text-${
    isDark ? 'base-lighter' : 'base-light'
  }`;
  const counterWatch = `${valueLength}/${maxLength}`;

  return (
    <div className="flex flex-row w-full">
      <TextField
        full
        chat
        ref={ref}
        darkMode={isDark}
        autoComplete="off"
        maxLength={noCounter ? undefined : maxLength}
        className="mr-8 min-h-32"
        endAdornment={
          noCounter ? undefined : <p className={counterClass}>{counterWatch}</p>
        }
        css={`
          div {
            max-height: 40px;
            input {
              max-height: 40px;
              min-height: 40px;
            }
          }
        `}
        {...props}
      />
      {onCancel ? (
        <>
          <PrimaryButton
            size="s"
            type="submit"
            isDark={isDark}
            disabled={submitDisabled}
            className="pl-4 pr-4 mr-4 min-h-40 min-w-40 max-h-40 max-w-40"
          >
            <Checkmark
              size={16}
              aria-hidden
              color={colors.white}
              className="fill-current ml-8 mr-8 min-w-16 max-w-16"
            />
          </PrimaryButton>
          <InverseButton
            size="s"
            isDark={isDark}
            onClick={onCancel}
            className="pl-4 pr-4 min-h-40 min-w-40 max-h-40 max-w-40"
          >
            <CloseSmall
              size={16}
              aria-hidden
              className="fill-current ml-8 mr-8 min-w-16 max-w-16"
              color={isDark ? colors.primaryLight : colors.primaryMain}
            />
          </InverseButton>
        </>
      ) : (
        <PrimaryButton
          isDark={isDark}
          size="s"
          className="pl-12 pr-12 h-40 w-40"
          type="submit"
          disabled={submitDisabled}
        >
          <Plus
            size={20}
            color={colors.white}
            className="fill-current min-w-20 max-w-20"
            aria-hidden
          />
        </PrimaryButton>
      )}
    </div>
  );
};

export default forwardRef(TextFieldActions);
